import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useLocation, Redirect } from 'react-router-dom'

import { useAuth } from '../../context/auth-context'
import { getRecipes } from '../../utils/api/recipe-client'
import { stripAccent, restoreScrollPosition } from '../../utils/helpers'
import RecipeListToolbar from './RecipeListToolbar'
import RecipeListGallery from './RecipeListGallery'
import RecipeListTable from './RecipeListTable'
import RecipeListEmpty from './RecipeListEmpty'
import FullPageLoader from '../FullPageLoader'

const RecipeList = () => {
  const location = useLocation()
  const { user } = useAuth()

  const [recipes, setRecipes] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewType, setViewType] = useState('gallery')
  const [searchStr, setSearchStr] = useState('')
  const [searchStrDebounced, setSearchStrDebounced] = useState('')

  const isMounted = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearchStrDebounced(searchStr), 300)
    return () => clearTimeout(timer)
  }, [searchStr])

  useEffect(() => {
    isMounted.current = true
    const fetchRecipes = async () => {
      setLoading(true)
      const results = await getRecipes()
      if (isMounted.current) {
        setRecipes(results)
        setLoading(false)
        restoreScrollPosition()
      }
    }
    fetchRecipes()
    return () => (isMounted.current = false)
  }, [])

  const filteredRecipes = useMemo(() => {
    if (!searchStrDebounced) return recipes || []

    const searchKeywords = stripAccent(searchStrDebounced)
      .toLowerCase()
      .split(' ')
      .map((keyword) => keyword.trim())
      .filter((keyword) => keyword.length > 0)

    return (
      recipes?.filter((recipe) => {
        const strIngredients = recipe?.ingredients?.length
          ? JSON.stringify(recipe.ingredients).toLowerCase()
          : ''
        const strInstructions = recipe?.instructions?.length
          ? JSON.stringify(recipe.instructions).toLowerCase()
          : ''
        const strFullRecipe = stripAccent(
          recipe?.title +
            '' +
            recipe?.description +
            '' +
            strIngredients +
            strInstructions +
            recipe?.sourceUrl +
            '',
        ).toLowerCase()

        return searchKeywords.every((keyword) => strFullRecipe.includes(keyword.toLowerCase()))
      }) || []
    )
  }, [searchStrDebounced, recipes])

  const getContent = () => {
    if (user.recipeCnt === 0 && location.pathname.indexOf('welcome-step') === -1) {
      return <Redirect to="/welcome-step-1" />
    } else if (loading) {
      return <FullPageLoader />
    } else {
      if (filteredRecipes.length > 0) {
        return viewType === 'gallery' ? (
          <RecipeListGallery recipes={filteredRecipes} />
        ) : (
          <RecipeListTable recipes={filteredRecipes} />
        )
      }
      return <RecipeListEmpty />
    }
  }

  return (
    <>
      <RecipeListToolbar
        viewType={viewType}
        setViewType={setViewType}
        onSearchChange={setSearchStr}
        recipes={filteredRecipes}
        loading={loading}
      />

      {getContent()}
    </>
  )
}

export { RecipeList as default }
